/**
 * 7日滑块导航组件
 * 替代垂直时间轴，实现横向周视图
 */

import { useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Check, AlertTriangle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import type { DaySliderItem } from "@/types/daily-dashboard";

interface DaySliderProps {
  days: DaySliderItem[];
  selectedDayIndex: number;
  onDaySelect: (dayIndex: number) => void;
  className?: string;
}

export function DaySlider({
  days,
  selectedDayIndex,
  onDaySelect,
  className,
}: DaySliderProps) {
  const { t } = useTranslation("assessments");
  const containerRef = useRef<HTMLDivElement>(null);
  const selectedRef = useRef<HTMLButtonElement>(null);

  // 自动滚动到选中项
  useEffect(() => {
    if (selectedRef.current && containerRef.current) {
      const container = containerRef.current;
      const selected = selectedRef.current;

      // 计算需要滚动的距离，使选中项居中
      const scrollLeft =
        selected.offsetLeft -
        container.offsetWidth / 2 +
        selected.offsetWidth / 2;

      container.scrollTo({
        left: Math.max(0, scrollLeft),
        behavior: "smooth",
      });
    }
  }, [selectedDayIndex]);

  return (
    <div
      ref={containerRef}
      className={cn(
        "w-full grid gap-2 overflow-x-auto pb-4 px-1 py-2",
        className,
      )}
      style={{
        gridTemplateColumns: `repeat(${days.length}, minmax(90px, 1fr))`,
      }}
    >
      {days.map((day) => {
        const isSelected = day.dayIndex === selectedDayIndex;
        const progress =
          day.tasksCount > 0 ? (day.completedCount / day.tasksCount) * 100 : 0;

        return (
          <button
            key={day.dayIndex}
            ref={isSelected ? selectedRef : undefined}
            onClick={() => onDaySelect(day.dayIndex)}
            className="transition-all hover:opacity-90 active:opacity-75"
          >
            <Card
              className={cn(
                "flex flex-col items-center justify-center p-2.5 h-[105px] transition-all",
                isSelected
                  ? "bg-primary text-primary-foreground shadow-lg border-primary ring-2 ring-primary/20"
                  : day.status === "completed"
                    ? "bg-emerald-50 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-800"
                    : "hover:border-primary/30",
              )}
            >
              {/* Day 标签 */}
              <Badge
                variant={isSelected ? "secondary" : "outline"}
                className={cn(
                  "mb-1.5 text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0 whitespace-nowrap",
                  isSelected
                    ? "bg-primary-foreground/10 text-primary-foreground border-primary-foreground/20"
                    : day.status === "completed"
                      ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-emerald-700"
                      : "",
                )}
              >
                {t("carePlan.day_format", { day: day.dayIndex })}
              </Badge>

              {/* 状态图标或进度 */}
              <div className="relative z-10 mb-1.5">
                {day.status === "completed" ? (
                  <div
                    className={cn(
                      "w-8 h-8 rounded-full flex items-center justify-center",
                      isSelected
                        ? "bg-primary-foreground/10 text-primary-foreground"
                        : "bg-emerald-500 text-white shadow-sm",
                    )}
                  >
                    <Check className="h-4 w-4" strokeWidth={3} />
                  </div>
                ) : day.isEmergency ? (
                  <div
                    className={cn(
                      "w-8 h-8 rounded-full flex items-center justify-center",
                      isSelected
                        ? "bg-primary-foreground/10 text-primary-foreground"
                        : "bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400",
                    )}
                  >
                    <AlertTriangle className="h-4 w-4" strokeWidth={2.5} />
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-1">
                    <div className="relative w-8 h-8 flex items-center justify-center">
                      {/* 进度环 */}
                      <svg
                        className="w-full h-full -rotate-90"
                        viewBox="0 0 32 32"
                      >
                        <circle
                          cx="16"
                          cy="16"
                          r="14"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          className={cn(
                            isSelected
                              ? "text-primary-foreground/20"
                              : "text-muted/20",
                          )}
                        />
                        <circle
                          cx="16"
                          cy="16"
                          r="14"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeDasharray={`${(progress / 100) * 87.96} 87.96`}
                          strokeLinecap="round"
                          className={cn(
                            isSelected
                              ? "text-primary-foreground"
                              : "text-primary",
                          )}
                        />
                      </svg>
                      {/* 进度数字 */}
                      <span
                        className={cn(
                          "absolute inset-0 flex items-center justify-center text-[9px] font-bold",
                          isSelected
                            ? "text-primary-foreground"
                            : "text-foreground",
                        )}
                      >
                        {day.completedCount}/{day.tasksCount}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* 阶段标签 */}
              <p
                className={cn(
                  "text-[9px] font-medium text-center line-clamp-2 w-full leading-tight px-0.5",
                  isSelected
                    ? "text-primary-foreground/90"
                    : day.status === "completed"
                      ? "text-emerald-700 dark:text-emerald-300"
                      : "text-muted-foreground",
                )}
                title={day.titleKey ? t(day.titleKey as any) : day.label}
              >
                {day.titleKey ? t(day.titleKey as any) : day.label}
              </p>
            </Card>
          </button>
        );
      })}
    </div>
  );
}

export default DaySlider;
